'use client'

import { useSearchParams } from 'next/navigation'
import { useEffect, useRef } from 'react'

import { getInitialSelectedVariant } from '@src/components/product-card/product-card.helpers'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import useFetch from '@src/quince-site-common/src/hooks/useFetch'
import PageBuilder, { ContainerConfig, PageContext } from '@src/quince-site-common/src/organisms/pageBuilder'
import wishListStateManager from '@src/quince-site-common/src/redux/globalStateManager/WishlistStateManager'
import { useAppSelector } from '@src/quince-site-common/src/redux/hooks'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import { ADDITIONAL_CUSTOM_SECTIONS } from '@src/quince-site-landing/src/templates/partnerPDPLandingPage/partnerPDPLandingPage.constant'
import SectionRenderer from '@src/SectionRenderer'
import { PageDataResponse } from '@src/server-side/api/api.types'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import WishlistHeading from './wishlist-components/wishlist-heading'
import WishlistItemList from './wishlist-components/wishlist-item-list'
import { fetchSharedWishList, fetchUserWishListProducts } from './wishlist.helpers'
import { SharedWishListDataType, WishlistItem } from './wishlist.types'

export interface ProductFromContentful {
  contentful_id: string
}

const Wishlist = ({ data, isMobile }: { data: PageDataResponse<PageContext<ContainerConfig>>; isMobile: boolean }) => {
  const pageData = data.context?.pageDataJson
  const isInit = useRef(false)

  const searchParams = useSearchParams()

  const id: string | null = searchParams.get('id')

  const {
    fetchData: fetchWishListItems,
    isLoading: isWishListLoading,
    response,
    hasError,
    error,
  } = useFetch<WishlistItem[], []>(fetchUserWishListProducts, {
    initialData: [],
    initialLoadingState: true,
  })

  const {
    fetchData: fetchShareWishListItems,
    isLoading: isShareWishListLoading,
    response: shareWishListResponse,
    hasError: shareWishListHasError,
    error: shareWishListError,
  } = useFetch<SharedWishListDataType, [string]>(fetchSharedWishList, {
    initialData: { name: '', wishListItems: [] },
    initialLoadingState: true,
  })

  const name = id ? shareWishListResponse?.name : undefined
  const wishListItems = id ? shareWishListResponse?.wishListItems : response

  const wishlistTotalItemsCount = useAppSelector(wishListStateManager, wishListStateManager.getWishlistTotalItemsCount)

  useEffect(() => {
    trackEvent(TELEMETRY_EVENT_NAMES.WISHLIST_PAGE_VIEW, {})
  }, [])

  useEffect(() => {
    if (hasError) {
      captureException(error, {
        module: MODULES.WISHLIST,
        feature: FEATURES.FETCH_WISHLIST,
        extraInfo: {
          // @i18n-ignore
          capturedFrom: 'fetchWishListItems in WishlistItemList',
        },
      })
    }
  }, [error, hasError])

  useEffect(() => {
    if (shareWishListHasError) {
      captureException(shareWishListError, {
        module: MODULES.WISHLIST,
        feature: FEATURES.FETCH_WISHLIST,
        extraInfo: {
          // @i18n-ignore
          capturedFrom: 'fetchShareWishListItems in WishlistItemList',
        },
      })
    }
  }, [shareWishListError, shareWishListHasError])

  useEffect(() => {
    if (!id) {
      fetchWishListItems().catch((e: unknown) => {
        console.error('Error while fetching wishList items, fetchWishListItems() function:: ', e)
      })
    }
  }, [wishlistTotalItemsCount, id, fetchWishListItems])

  useEffect(() => {
    if (id) {
      fetchShareWishListItems(id).catch((e: unknown) => {
        console.error('error while fetching shared wishList items, fetchShareWishListItems() function:: ', e)
      })
    }
  }, [fetchShareWishListItems, id])

  const sectionRenderer = SectionRenderer({
    // @i18n-ignore
    tracker: 'landingPage',
    additionalCustomSections: ADDITIONAL_CUSTOM_SECTIONS,
    pageSubType: pageData?.pageSubType,
  })

  const isSelfWishlist = !id
  const isLoading = id ? isShareWishListLoading : isWishListLoading

  const wishlistProductList =
    wishListItems?.map(productCard => {
      const selectedCardVariant = getInitialSelectedVariant(productCard.product)
      const selectedColor = selectedCardVariant?.displayConfig.value
      return {
        product_id: productCard.product.relatedIds.spid,
        color: selectedColor,
      }
    }) ?? []

  useEffect(() => {
    if (isInit.current) return
    if (!isLoading) {
      const eventToFire = isSelfWishlist
        ? TELEMETRY_EVENT_NAMES.WISHLIST_LOAD
        : TELEMETRY_EVENT_NAMES.SHARED_WISHLIST_LOAD
      trackEvent(eventToFire, {
        wishlistProductList: wishlistProductList,
      })
      isInit.current = true
    }
  }, [wishListItems, isLoading, isSelfWishlist])

  return (
    <>
      <WishlistHeading isSelfWishlist={isSelfWishlist} name={name} wishListProductList={wishlistProductList} />
      <WishlistItemList
        items={wishListItems}
        isLoading={isLoading}
        sectionConfig={{
          // @i18n-ignore
          tracker: 'landingPage',
        }}
        isSelfWishlist={isSelfWishlist}
      />
      {pageData && (
        <PageBuilder
          context={pageData}
          selfContainerRenderer={sectionRenderer}
          isMobile={isMobile}
          className={'hide-horizontal-scroll'}
        />
      )}
    </>
  )
}

export default Wishlist
